import { Injectable, computed, inject, signal } from '@angular/core';
import { IconName } from '../../shared/components/icon/icon-registry';
import { ApiLanguage, LANGUAGE_FLAG, LANGUAGE_LABEL } from '../models/api.model';
import { courseIcon, courseSkill, skillAccent } from '../models/course-presentation';
import {
  isLearnableCourseEnrollment,
  isRegisteredCourseEnrollment,
  StudentCurriculumEnrollment,
} from '../models/curriculum-api.model';
import { CurriculumApiService } from './curriculum-api.service';

export interface CourseOption {
  id: string;
  name: string;
  level: string;
  color: string;
  icon: IconName;
  flag: string;
}

const STORAGE_KEY = 'polilo.currentCourse';

/** Shown before enrollments load (or when the learner has none yet). */
const PLACEHOLDER: CourseOption = {
  id: '',
  name: 'No course yet',
  level: '',
  color: 'var(--color-ocean-blue)',
  icon: 'book',
  flag: '🌊',
};

/**
 * The course a learner is currently working inside, backed by their active
 * enrollments. The shell's switcher writes to it; screens read it to scope
 * their content.
 */
@Injectable({ providedIn: 'root' })
export class CourseContextService {
  private readonly api = inject(CurriculumApiService);

  private readonly options = signal<CourseOption[]>([]);
  private readonly enrollmentList = signal<StudentCurriculumEnrollment[]>([]);
  private readonly currentId = signal<string | null>(this.restore());
  private readonly loadingFlag = signal(false);
  private loaded = false;

  readonly courses = computed<readonly CourseOption[]>(() => this.options());
  /** True while the enrollment fetch is in flight — screens skeleton on it. */
  readonly loading = computed(() => this.loadingFlag());
  readonly enrollments = computed<readonly StudentCurriculumEnrollment[]>(() =>
    this.enrollmentList(),
  );

  /** Course ids that still appear on Home, Courses, To Do, and Lexicons. */
  readonly panelCourseIds = computed(() => {
    const ids = this.enrollmentList()
      .filter((row) => isRegisteredCourseEnrollment(row.enrollmentStatus))
      .map((row) => row.curriculumCourseId);
    return new Set(ids);
  });

  readonly current = computed<CourseOption>(() => {
    const list = this.options();
    return list.find((c) => c.id === this.currentId()) ?? list[0] ?? PLACEHOLDER;
  });

  /** Language of the selected course (same enum as topics / CurriculumCourse). */
  readonly currentLanguage = computed<ApiLanguage | undefined>(() => {
    const id = this.current().id;
    if (!id) {
      return undefined;
    }
    return this.enrollmentList().find((e) => e.curriculumCourseId === id)?.curriculumCourse
      ?.language;
  });

  /**
   * Prefer this course after login when the API returns `currentCourse`.
   * Safe to call before enrollments finish loading — selection applies once options exist.
   */
  preferCourse(courseId: string | null | undefined): void {
    if (!courseId) {
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, courseId);
    } catch {
      /* ignore */
    }
    this.currentId.set(courseId);
    if (this.options().some((c) => c.id === courseId)) {
      this.select(courseId);
    }
  }

  /** Fetch the learner's enrollments; call from the authenticated shell. */
  load(force = false): void {
    if (this.loaded && !force) {
      return;
    }
    this.loaded = true;
    this.loadingFlag.set(true);
    this.api.enrollments(1, 100).subscribe({
      next: (page) => {
        this.syncEnrollments(page.items);
        const preferred = this.currentId();
        if (preferred && this.options().some((c) => c.id === preferred)) {
          this.select(preferred);
        }
        this.loadingFlag.set(false);
      },
      error: () => {
        this.loaded = false;
        this.loadingFlag.set(false);
      },
    });
  }

  /** Merge a freshly created or updated enrollment into local state. */
  applyEnrollment(enrollment: StudentCurriculumEnrollment): void {
    const items = [...this.enrollmentList()];
    const index = items.findIndex((e) => e.curriculumCourseId === enrollment.curriculumCourseId);
    if (index >= 0) {
      items[index] = enrollment;
    } else {
      items.push(enrollment);
    }
    this.syncEnrollments(items);
    if (isLearnableCourseEnrollment(enrollment.enrollmentStatus)) {
      this.select(enrollment.curriculumCourseId);
    }
  }

  removeEnrollment(enrollmentId: string): void {
    this.syncEnrollments(this.enrollmentList().filter((row) => row.id !== enrollmentId));
  }

  replaceEnrollments(items: StudentCurriculumEnrollment[]): void {
    this.syncEnrollments(items);
  }

  isOnStudentPanel(courseId?: string | null): boolean {
    return !!courseId && this.panelCourseIds().has(courseId);
  }

  isLexiconOnStudentPanel(courseIds?: readonly string[] | null): boolean {
    const panel = this.panelCourseIds();
    if (!courseIds?.length) {
      return panel.size > 0;
    }
    return courseIds.some((id) => panel.has(id));
  }

  private syncEnrollments(items: StudentCurriculumEnrollment[]): void {
    this.enrollmentList.set(items);
    const learnable = items
      .filter((e) => isLearnableCourseEnrollment(e.enrollmentStatus))
      .map((e) => this.toOption(e));
    this.options.set(learnable);
    const current = this.currentId();
    if (current && !learnable.some((course) => course.id === current)) {
      const next = learnable[0]?.id ?? null;
      this.currentId.set(next);
      try {
        if (next) {
          localStorage.setItem(STORAGE_KEY, next);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        /* storage unavailable — selection stays in memory only */
      }
    }
  }

  select(id: string): void {
    if (this.options().some((c) => c.id === id)) {
      this.currentId.set(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* storage unavailable — selection stays in memory only */
      }
    }
  }

  private toOption(enrollment: StudentCurriculumEnrollment): CourseOption {
    const course = enrollment.curriculumCourse;
    const id = enrollment.curriculumCourseId;
    const language = course?.language;
    return {
      id,
      name: course?.title ?? 'Course',
      level: language !== undefined ? LANGUAGE_LABEL[language] : '',
      color: course?.color || skillAccent(courseSkill(id, enrollment.tags)),
      icon: courseIcon(id),
      flag: language !== undefined ? LANGUAGE_FLAG[language] : '🌊',
    };
  }

  private restore(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
